import { Box, Card, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { AppUtils } from "source/framework/appUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import * as taskService from "../../../models/services/taskService";
import { ActionContainer } from "../../framework/ActionContainer";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { GridRow } from "../../framework/Grid/GridRow";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { TaskTypeDialogPreviewType, TaskTypeDialogPreviewWeb } from "../TaskTypeDialog/TaskTypeDialogPreview/TaskTypeDialogPreviewWeb";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { getFirstSelectableTreeListItem, ITreeListItem, TreeList } from "source/components/framework/List/TreeList";
import { TaskRepetitionType } from "ui/Scripts/source/models/enums";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { ISessionState } from "source/models/session/sessionState";
import { StoreUtils } from "source/models/store/storeUtils";

interface TaskTypeNewDialogProps {
    companyId: string;
    onOk: (id: string) => void;
    onCancel: () => void;
}

interface TaskTypeNewDialogState {
    allSystemTaskTypes: ITaskEditTypeItem[];
    allCompanyTaskTypes: ITaskEditTypeItem[];
    listItems: ITreeListItem[];
    taskType: ITaskEditTypeItem;
    selectedId: string;
    filter: string;
    headerDataRow: ITaskDataRow;
    formDataRow: ITaskDataRow;
    lineDataRow: ITaskDataRow;
    initialized: boolean;
    isLoading: boolean;
}

export const TaskTypeNewDialog = (
    props: TaskTypeNewDialogProps
) => {
    const createEmptyTaskType = () => {
        const result = new TaskEditTypeItem();
        result.id = Base.emptyGuid;
        result.code = `'${Translations.EmptyForm}'`;
        return result;
    };

    const getDataRows = (taskType: ITaskEditTypeItem) => {
        const headerDataRow = new TaskDataRow();
        const formDataRow = new TaskDataRow();
        const lineDataRow = new TaskDataRow();
        if (taskType) {
            headerDataRow.cols = TaskDataRowCol.initializeValuesByType(taskType.headerCols, []);
            formDataRow.cols = TaskDataRowCol.initializeValuesByType(taskType.formCols, []);
            lineDataRow.cols = TaskDataRowCol.initializeValuesByType(taskType.lineCols, []);
        }
        return {
            headerDataRow,
            formDataRow,
            lineDataRow
        };
    };

    const [state, setState] = useState<TaskTypeNewDialogState>({
        allSystemTaskTypes: [createEmptyTaskType()],
        allCompanyTaskTypes: [],
        listItems: [],
        taskType: createEmptyTaskType(),
        selectedId: Base.emptyGuid,
        filter: "",
        ...getDataRows(createEmptyTaskType()),
        initialized: false,
        isLoading: false,
    });
    
    const taskEditTypeItemsToTreeListItems = (taskTypes: ITaskEditTypeItem[]): ITreeListItem[] => {
        return taskTypes.map(item => {
            return {
                id: item.id,
                title: item.code,
                children: []
            };
        });
    };

    const getSubTreeListItemsChildren = (sessionState: ISessionState, taskTypes: ITaskEditTypeItem[]): ITreeListItem[] => {
        const result: ITreeListItem[] = [];
        let unGroupedTreeListItems = taskEditTypeItemsToTreeListItems(taskTypes);
        // if (!sessionState?.isSuperAdmin) return unGroupedTreeListItems; // Only super admin can see groups
        const groups = Base.getUniqueStringItems(taskTypes.map(t => t.group)).filter(g => g).sort();
        if (groups.length < 1) return unGroupedTreeListItems;
        for (const group of groups) {
            const groupTaskTypes = taskTypes.filter(taskType => taskType.group === group);
            if (groupTaskTypes.length < 1) continue;
            const treeListItem = {
                id: group,
                title: group,
                children: taskEditTypeItemsToTreeListItems(groupTaskTypes)
            };
            result.push(treeListItem);
            unGroupedTreeListItems = unGroupedTreeListItems.filter(i => !groupTaskTypes.some(g => g.id === i.id));
        }
        if (unGroupedTreeListItems.length > 0) {
            result.push({
                id: Translations.OtherForms,
                title: Translations.OtherForms,
                children: unGroupedTreeListItems
            });
        }
        return result;
    };

    const getSubTreeListItems = (sessionState: ISessionState, taskTypes: ITaskEditTypeItem[]) => {
        const notRepeatedTaskTypes = taskTypes.filter(taskType => taskType.taskRepetitionType === TaskRepetitionType.NotRepeated);
        const repeatedTaskTypes = taskTypes.filter(taskType => taskType.taskRepetitionType === TaskRepetitionType.Repeated);
        let result: ITreeListItem[] = [];
        if (notRepeatedTaskTypes.length > 0) {
            result.push({
                id: Translations.OneTimeForm,
                title: Translations.OneTimeForm,
                divider: true,
                children: []
            });
            result = result.concat(getSubTreeListItemsChildren(sessionState, notRepeatedTaskTypes));
        }
        if (repeatedTaskTypes.length > 0) {
            result.push({
                id: Translations.RowForm,
                title: Translations.RowForm,
                divider: true,
                children: []
            });
            result = result.concat(getSubTreeListItemsChildren(sessionState, repeatedTaskTypes));
        }
        return result;
    };

    const getTreeListItems = (allSystemTaskTypes: ITaskEditTypeItem[], allCompanyTaskTypes: ITaskEditTypeItem[], searchFilter: string): ITreeListItem[] => {
        const result: ITreeListItem[] = [];
        const lowSearchFilter = searchFilter.toLocaleLowerCase();
        const sessionState = StoreUtils.getSessionState();
        // Add empty task type to the top of the list
        const emptyTaskType = createEmptyTaskType();
        if (!lowSearchFilter || emptyTaskType.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1) {
            result.push({ id: emptyTaskType.id, title: emptyTaskType.code, children: [] });
        }
        // Add company task types
        const companyTaskTypes = lowSearchFilter
            ? allCompanyTaskTypes.filter(i => i.code.toLocaleLowerCase().indexOf(searchFilter) > -1)
            : allCompanyTaskTypes.slice(0);
        if (companyTaskTypes.length > 0) {
            result.push({ id: Translations.TaskTypes, title: Translations.TaskTypes, children: getSubTreeListItems(sessionState, companyTaskTypes) });
        }
        // Add system task types
        const systemTaskTypes = lowSearchFilter
            ? allSystemTaskTypes.filter(i => i.code.toLocaleLowerCase().indexOf(searchFilter) > -1)
            : allSystemTaskTypes.slice(0);
        if (systemTaskTypes.length > 0) {
            result.push({ id: Translations.SharedTaskTypes, title: Translations.SharedTaskTypes, children: getSubTreeListItems(sessionState, systemTaskTypes) });
        }
        return result;
    };

    const loadItems = async () => {
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => taskService.getTaskTypeNew(props.companyId), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const allSystemTaskTypes = result.systemTaskTypes;
        const allCompanyTaskTypes = result.companyTaskTypes;
        const listItems = getTreeListItems(allSystemTaskTypes, allCompanyTaskTypes, state.filter);
        const selectedId = getFirstSelectableTreeListItem(listItems, Base.emptyGuid)?.id ?? state.selectedId;
        const taskType = allSystemTaskTypes.union(allCompanyTaskTypes).find(i => i.id === selectedId) ?? state.taskType;
        setState((old) => ({
            ...old,
            initialized: true,
            allSystemTaskTypes,
            allCompanyTaskTypes,
            listItems,
            taskType,
            selectedId: taskType.id,
            ...getDataRows(taskType)
        }));
    };

    useEffect(() => {
        loadItems();
    }, []);

    const changeFilter = (searchFilter: string) => {
        let systemTaskTypes: ITaskEditTypeItem[] = [];
        let companyTaskTypes: ITaskEditTypeItem[] = [];
        if (searchFilter) {
            const lowSearchFilter = searchFilter.toLocaleLowerCase();
            systemTaskTypes = state.allSystemTaskTypes.filter(i => i.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1);
            companyTaskTypes = state.allCompanyTaskTypes.filter(i => i.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1);
        } else {
            systemTaskTypes = state.allSystemTaskTypes.slice(0);
            companyTaskTypes = state.allCompanyTaskTypes.slice(0);
        }
        const taskType = systemTaskTypes.concat(companyTaskTypes).find(i => i.id === state.selectedId);
        setState((old) => ({
            ...old,
            filter: searchFilter,
            taskType,
            selectedId: taskType ? state.selectedId : "",
            ...getDataRows(taskType),
            listItems: getTreeListItems(state.allSystemTaskTypes, state.allCompanyTaskTypes, searchFilter)
        }));
    };

    const handleSetSelectedId = (selectedId: string) => {
        const taskType = state.allSystemTaskTypes.concat(state.allCompanyTaskTypes).find(i => i.id === selectedId);
        setState((old) => ({
            ...old,
            selectedId,
            taskType,
            ...getDataRows(taskType)
        }));
    };

    return (
        <Dialog
            id="task-form-designer-new-dialog"
            maxWidth="xl"
            maxHeight="100%"
            title={Translations.AddTaskTypeFromTemplate}
            body={
                <GridRow height="100%" sx={{ height: "calc(100% + 16px)" }}>
                    <Grid item sm={4} height="100%">
                        <VerticalContainer
                            before={
                                <ActionContainer
                                    leftItems={
                                        <ListSearchFilter
                                            searchFilter={state.filter}
                                            onSearchClick={changeFilter}
                                        />
                                    }
                                />
                            }
                            main={
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        height: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderRadius: ".5rem",
                                            height: "100%",
                                            overflowY: "auto"
                                        }}
                                    >
                                        <TreeList
                                            items={state.listItems}
                                            selectedId={state.selectedId}
                                            onSelect={item => handleSetSelectedId(item.id)}
                                        />
                                    </Box>
                                </Card>
                            }
                        />
                    </Grid>
                    <Grid item sm={8} height="100%">
                        <TaskTypeDialogPreviewWeb
                            previewType={TaskTypeDialogPreviewType.Web}
                            height="100%"
                            title={Translations.Preview}
                            taskType={state.taskType}
                            headerDataRow={state.headerDataRow}
                            formDataRow={state.formDataRow}
                            lineDataRow={state.lineDataRow}
                            onFormItemClick={() => {}}
                            onHeaderItemClick={() => {}}
                            onUpdateFormDataRowCols={() => {}}
                            onUpdateHeaderDataRowCols={() => {}}
                        />
                    </Grid>
                </GridRow>
            }
            actions={
                <DialogButtons
                    saveDisabled={!state.selectedId}
                    saveTitle={Translations.Next}
                    onCancel={props.onCancel}
                    onSave={() => Promise.resolve(props.onOk(state.selectedId))}
                />
            }
            onClose={props.onCancel}
        />
    );
};
